import { Component, type ReactNode } from "react";
import { useRouteError } from "react-router-dom";
import { chunkFailureCause, isChunkLoadError, reloadForUpdate, type ChunkCause } from "@/lib/appUpdate";
import "./updateBanner.css";

const TEXT: Record<ChunkCause, string> = {
  offline: "Нет связи с сервером — обновите страницу, когда она появится.",
  update: "Вышла новая версия — обновите страницу.",
  // Сервер на месте и сборка та же — сбой на запросе или выкладка на полпути.
  retry: "Обновите страницу, а если не поможет — попробуйте через минуту.",
};

// Кусок сборки не догрузился, а сама страница перезагружаться не стала
// (lib/appUpdate, chunkErrorVerdict: идёт звонок или загрузка, сервер не
// отвечает, только что уже перезагружались или пользователь остался на
// «Покинуть сайт?»). Раньше на этом месте был белый экран или «Unexpected
// Application Error» React Router'а.
export function ChunkFailed({ title = "Страница не загрузилась", error }: { title?: string; error?: unknown }) {
  // Причину знает chunkFailed — он спрашивал сервер. Ошибка прошла мимо него —
  // хотя бы по navigator.onLine: без сети кусок не пришёл бы и из той же
  // сборки, и про новую версию врать незачем (установленное приложение на
  // телефоне открывают где угодно).
  const cause =
    chunkFailureCause(error) ?? (typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : null);
  return (
    <div className="chunk-failed" role="alert">
      <b>{title}</b>
      <span>{cause ? TEXT[cause] : "Скорее всего, вышла новая версия — обновите страницу."}</span>
      <button type="button" className="btn-primary" onClick={reloadForUpdate}>
        Обновить
      </button>
    </div>
  );
}

/** errorElement страниц роутера. Любая другая ошибка — дальше, как и раньше:
 *  брошенная из errorElement, она уходит к стандартному экрану React Router. */
export function RouteError() {
  const error = useRouteError();
  if (!isChunkLoadError(error)) throw error;
  return <ChunkFailed error={error} />;
}

/** То же для кусков, которые грузит React.lazy вне роутера (карточка
 *  кандидата, вкладка «Чаты» вакансии, публичная страница, заглушка «только на
 *  телефоне»). `className` — рамка, в которой показать ошибку: там же, где
 *  стояла заглушка загрузки; `extra` — что ещё оставить в рамке (у карточки —
 *  «Закрыть»: без неё лист во весь экран не закрыть ничем). */
export class ChunkBoundary extends Component<
  { children: ReactNode; className?: string; title?: string; extra?: ReactNode },
  { error: unknown }
> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (error == null) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    return (
      <div className={this.props.className}>
        {this.props.extra}
        <ChunkFailed title={this.props.title} error={error} />
      </div>
    );
  }
}
